import { useEffect, useState, type CSSProperties } from 'react'
import { ferry, type Passport as PassportView, type StampId } from '../lib/ferry'
import { t, type StringKey } from '../lib/i18n'
import Icon, { type IconName } from './Icon'

const ICON: Record<StampId, IconName> = {
  first: 'star', vieques: 'island', culebra: 'island', bothIslands: 'route',
  sunrise: 'sunrise', sunset: 'moon', family: 'people', roundTrip: 'loop',
  regular: 'anchor', islander: 'ferry',
}

/* Each stamp sits at its own slight angle, the way a rubber stamp lands.
   Fixed per stamp rather than random, so the page does not shuffle on reload. */
const TILT: Record<StampId, number> = {
  first: -6, vieques: 4, culebra: -3, bothIslands: 7, sunrise: -8,
  sunset: 3, family: -4, roundTrip: 6, regular: -2, islander: 5,
}

const ORDER: StampId[] = [
  'first', 'vieques', 'culebra', 'bothIslands', 'sunrise',
  'sunset', 'family', 'roundTrip', 'regular', 'islander',
]

/** What a passport looks like before anything is stamped — for signed-out visitors. */
const BLANK: PassportView = {
  crossings: 0, travellers: 0, earned: 0,
  stamps: ORDER.map((id) => ({ id, earned: false, earnedAt: null })),
}

/**
 * The strip on the home screen.
 *
 * Small on purpose and above the board: the schedule is still the answer to the
 * question people open the app with, so this is one row they can scroll past —
 * but a feature that only lives two taps deep under My Trips is a feature
 * nobody finds. Signed out, it shows the blank passport: the visitor on the
 * dock sees there is something to collect before being asked for anything.
 */
export function PassportStrip({ signedIn, onOpen }: { signedIn: boolean; onOpen?: () => void }) {
  const [view, setView] = useState<PassportView | null>(signedIn ? null : BLANK)

  useEffect(() => {
    if (!signedIn) { setView(BLANK); return }
    ferry.passport().then(setView).catch(() => setView(BLANK))
  }, [signedIn])

  if (!view) return null

  return (
    <section className="card passport-strip">
      <div className="passport-strip-head">
        <strong className="with-icon"><Icon name="globe" size={18} />{t('passport.title')}</strong>
        <span className="muted small">{view.earned}/{view.stamps.length} {t('passport.stamps')}</span>
        {signedIn && onOpen && (
          <button className="link" onClick={onOpen}>{t('passport.open')}</button>
        )}
      </div>
      <ul className="stamps-row">
        {view.stamps.map((s) => (
          <li
            key={s.id}
            className={`stamp${s.earned ? ' earned' : ''}`}
            style={{ '--tilt': `${TILT[s.id]}deg` } as CSSProperties}
            title={`${t(`stamp.${s.id}` as StringKey)} — ${t(`stamp.${s.id}.hint` as StringKey)}`}
          >
            <span className="stamp-ring"><Icon name={ICON[s.id]} size={18} /></span>
          </li>
        ))}
      </ul>
      <p className="muted small passport-strip-foot">
        {signedIn ? (view.crossings === 0 ? t('passport.empty') : t('passport.sub')) : t('passport.teaser')}
      </p>
    </section>
  )
}

const stampedOn = (ms: number) =>
  new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'America/Puerto_Rico' }).format(ms)

/**
 * The island passport.
 *
 * Stamps are earned at the gate — the server only counts bookings the scan
 * marked `boarded` — so this screen never has to explain why a cancelled trip
 * still shows a stamp. Locked stamps are drawn as faint outlines rather than
 * hidden: seeing what there is to collect is the whole point.
 */
export default function Passport() {
  const [view, setView] = useState<PassportView | null>(null)

  useEffect(() => {
    ferry.passport().then(setView).catch(() => setView(null))
  }, [])

  // Nice to have, never in the way: a failed load hides the card rather than
  // putting an error above the trips somebody actually came here for.
  if (!view) return null

  const next = view.stamps.find((s) => !s.earned)

  return (
    <section className="card passport">
      <h2 className="with-icon"><Icon name="globe" size={20} />{t('passport.title')}</h2>
      <p className="muted small">{t('passport.sub')}</p>

      <div className="passport-stats">
        <div><strong>{view.crossings}</strong><span>{t('passport.crossings')}</span></div>
        <div><strong>{view.travellers}</strong><span>{t('passport.travellers')}</span></div>
        <div><strong>{view.earned}/{view.stamps.length}</strong><span>{t('passport.stamps')}</span></div>
      </div>

      {view.crossings === 0 && <p className="passport-empty">{t('passport.empty')}</p>}

      <ul className="stamps">
        {view.stamps.map((s) => (
          <li
            key={s.id}
            className={`stamp${s.earned ? ' earned' : ''}`}
            style={{ '--tilt': `${TILT[s.id]}deg` } as CSSProperties}
          >
            <span className="stamp-ring"><Icon name={ICON[s.id]} size={26} /></span>
            <strong>{t(`stamp.${s.id}` as StringKey)}</strong>
            <span className="muted small">
              {s.earned && s.earnedAt
                ? `${t('passport.earned')} · ${stampedOn(s.earnedAt)}`
                : s.progress ? `${s.progress.have}/${s.progress.need}` : t(`stamp.${s.id}.hint` as StringKey)}
            </span>
          </li>
        ))}
      </ul>

      {next && view.crossings > 0 && (
        <p className="passport-next">
          <Icon name="chevron" size={14} />
          <span><b>{t('passport.next')}:</b> {t(`stamp.${next.id}` as StringKey)} — {t(`stamp.${next.id}.hint` as StringKey)}</span>
        </p>
      )}
    </section>
  )
}
